import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import { Header, PillTabs, PullToRefresh, ResizedImage, type PillTab } from '@ah/design/mobile';
import { Trophy, Sparkles } from 'lucide-react';
import {
  CIRCLES_BY_TYPE_QUERY,
  circleDisplayName,
  type Circle,
} from '../shared/index.ts';
import { CircleListSkeleton, ErrorRetry } from './_parts.tsx';

const TABS: PillTab[] = [
  { to: '/games', label: 'My Leagues', end: true },
  { to: '/games/suggested', label: 'Suggested' },
];

/**
 * Recommended Leagues — eligible leagues sorted by popularity. The server
 * (resolver `circles`) handles the sort (member_count DESC, name ASC).
 */
export function SuggestedLeagues() {
  const gql = useGql();
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['leagues', 'suggested'],
    queryFn: async () => {
      const res = await gql.request<{ circles: Circle[] }>(CIRCLES_BY_TYPE_QUERY, {
        type: 'league',
        limit: 50,
      });
      return res.circles;
    },
  });

  return (
    <div className="fixed inset-0 flex flex-col bg-background">
      <Header title="Leagues" />
      <PillTabs tabs={TABS} />
      <PullToRefresh className="flex-1 px-3 py-3" onRefresh={() => refetch()}>
        {isLoading ? (
          <CircleListSkeleton />
        ) : error ? (
          <ErrorRetry label="Could not load suggestions." onRetry={() => refetch()} />
        ) : !data || data.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground">
            <Sparkles size={32} className="mx-auto mb-2 opacity-50" />
            <p className="text-sm font-medium">No leagues to suggest right now.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {data.map((c) => (
              <SuggestedLeagueRow key={c.id} circle={c} />
            ))}
          </div>
        )}
      </PullToRefresh>
    </div>
  );
}

function SuggestedLeagueRow({ circle }: { circle: Circle }) {
  const cover = circle.coverUrl;
  return (
    <Link
      to={`/circles/${circle.id}`}
      className="flex gap-3 items-center p-3 bg-card rounded-xl border border-border active:bg-accent"
    >
      <div className="w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-muted flex items-center justify-center">
        {cover ? (
          <ResizedImage src={cover} renderWidth={56} fallback={false} alt="" className="w-full h-full object-cover" />
        ) : (
          <Trophy size={20} className="text-muted-foreground/60" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-sm font-semibold truncate">
          {circleDisplayName(circle.displayTitle ?? circle.name)}
        </h2>
        {circle.description ? (
          <p className="text-[12px] text-muted-foreground truncate mt-0.5">
            {circle.description}
          </p>
        ) : null}
      </div>
    </Link>
  );
}
